/**
 * Contrato da camada de dados do painel.
 *
 * Duas implementações atendem a esta interface: `firestoreSource` (produção) e `demoSource`
 * (localStorage). O motivo é o mesmo do store injetável do backend — o painel precisa ser
 * utilizável e verificável antes de o Firebase estar provisionado, e sem escrever código que
 * depois seria jogado fora.
 *
 * Regra que as duas implementações respeitam igualmente: **valor de segredo nunca é lido de
 * volta**. O painel conhece só os nomes (`secretNames`). Se o modo demo devolvesse os valores,
 * a interface seria construída em cima de uma garantia que produção não oferece.
 */

import type { Appearance, Flow, WebchatVersion } from '@merlin/flow-engine';

export interface PublishedVersion extends WebchatVersion {
  version: number;
}

export interface WebchatDoc {
  id: string;
  ownerId: string;
  name: string;
  publicKey: string;
  authorizedDomains: string[];
  /** Destinos permitidos ao webhook de backend quando a URL tem variável no host. */
  outboundAllowlist: string[];
  /** Só os nomes. Os valores ficam server-side, ilegíveis pelo cliente. */
  secretNames: string[];
  /**
   * Se as variáveis coletadas devem ser guardadas como lead. Ausente conta como ligado — o produto
   * existe para o contato ir a algum lugar, e descartá-lo em silêncio seria pior surpresa.
   */
  collectLeads?: boolean;
  /**
   * Teto de conversas por mês. **Ausente** vale `DEFAULT_CONVERSATION_LIMIT`; **`null`** é ilimitado.
   * Os três estados são distintos — ver `resolveConversationLimit` no engine.
   */
  conversationLimit?: number | null;
  /**
   * Teto de **requisições de config** por mês, que é o que de fato gera custo.
   *
   * Diferente do de conversas num ponto: **ausente é ilimitado**, não um padrão ligado — ver
   * `resolveRequestLimit` no engine para o porquê.
   */
  requestLimit?: number | null;
  /**
   * Estado administrativo, **somente leitura** no painel.
   *
   * De propósito não existe em `DraftPatch`: as Rules recusam qualquer escrita neste campo pelo cliente,
   * e é isso que faz a suspensão ser a única decisão que o dono não desfaz. O `updateDoc` do autosave
   * faz merge, então omitir aqui basta — o campo sobrevive intocado.
   */
  status?: string;
  draft: WebchatVersion;
  published: PublishedVersion | null;
  updatedAt: number | null;
}

/** Os dois contadores do mês, que vivem no mesmo documento `usage/{mês}` — uma leitura serve os dois. */
export interface MonthlyUsage {
  conversations: number;
  /** Exato. Contado em memória pelo servidor e gravado em lote a cada 30s — pode faltar o último lote. */
  requests: number;
}

export interface WebchatSummary {
  id: string;
  name: string;
  publicKey: string;
  publishedVersion: number | null;
  updatedAt: number | null;
}

export interface DraftPatch {
  name?: string;
  authorizedDomains?: string[];
  outboundAllowlist?: string[];
  collectLeads?: boolean;
  /** `null` grava "ilimitado". Não confundir com omitir a chave, que é "não mexer". */
  conversationLimit?: number | null;
  /** Idem. Aqui `null` **e** ausente valem ilimitado no servidor, mas omitir aqui é "não mexer". */
  requestLimit?: number | null;
  appearance?: Appearance;
  flow?: Flow;
}

/** Publicação recusada pela validação server-side. `blockers` é o que a UI lista para o usuário. */
export class PublishBlockedError extends Error {
  constructor(
    message: string,
    readonly blockers: string[],
  ) {
    super(message);
    this.name = 'PublishBlockedError';
  }
}

/** Um contato capturado. Dado pessoal: só o dono do webchat lê, e ele pode apagar. */
export interface LeadEntry {
  id: string;
  variables: Record<string, unknown>;
  transcript: { role: 'bot' | 'visitor' | 'system'; text: string }[];
  pageUrl?: string;
  sdkVersion?: string;
  createdAt: number | null;
}

/** Contadores agregados de um dia. Ver `apps/backend/src/core/events.ts`. */
export interface AnalyticsDay {
  day: string;
  starts: number;
  completions: number;
  abandons: number;
  nodeEnters: Record<string, number>;
  nodeAbandons: Record<string, number>;
}

/**
 * Uma publicação registrada. Só metadado: o fluxo em si é buscado por `getVersion` na hora de
 * restaurar, para a lista não carregar o conteúdo de 20 versões sem necessidade.
 */
export interface VersionEntry {
  version: number;
  publishedAt: number | null;
  nodeCount: number;
}

export interface DataSource {
  readonly kind: 'firestore' | 'demo';
  list(): Promise<WebchatSummary[]>;
  get(id: string): Promise<WebchatDoc | null>;
  create(name: string): Promise<string>;
  saveDraft(id: string, patch: DraftPatch): Promise<void>;
  /**
   * Promove o draft. `unchanged` sai `true` quando o conteúdo é idêntico ao publicado: nesse caso
   * **nenhuma versão nova é criada**, e a versão devolvida é a que já estava no ar. Histórico cheio de
   * entradas iguais não serve para achar onde algo mudou.
   */
  publish(id: string): Promise<{ version: number; unchanged: boolean }>;
  remove(id: string): Promise<void>;
  upsertSecret(id: string, name: string, value: string): Promise<void>;
  deleteSecret(id: string, name: string): Promise<void>;
  uploadButtonImage(id: string, file: File): Promise<string>;
  /** Leads mais recentes primeiro. Leitura garantida pelas Rules ao dono do webchat. */
  listLeads(id: string, limit?: number): Promise<LeadEntry[]>;
  deleteLead(id: string, leadId: string): Promise<void>;
  /** Últimos `days` dias de contadores, do mais recente para o mais antigo. */
  getAnalytics(id: string, days?: number): Promise<AnalyticsDay[]>;
  /**
   * Conversas iniciadas no mês (`AAAA-MM`), o número que o teto de gastos compara.
   *
   * Não sai de somar `getAnalytics`: o servidor conta num documento mensal próprio, e o painel
   * precisa mostrar **o mesmo número** que o `getConfig` usa para decidir. Somar aqui daria uma
   * segunda conta, que divergiria na virada do dia.
   */
  getMonthlyUsage(id: string, month: string): Promise<MonthlyUsage>;
  /** Publicações registradas, da mais nova para a mais antiga. */
  listVersions(id: string, limit?: number): Promise<VersionEntry[]>;
  /** O conteúdo de uma versão, para restaurar no rascunho. */
  getVersion(id: string, version: number): Promise<WebchatVersion | null>;
}

/** `pk_` + aleatório. É a chave que vai no `data-webchat-id` do snippet. */
export function generatePublicKey(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const body = Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('');
  return `pk_${body.slice(0, 26)}`;
}
